import { ArrowDownward, ArrowUpward } from "@mui/icons-material";
import { Box, Grid, Link, Tooltip } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { addDays, format } from "date-fns";
import { getItems } from "../../../api/items";
import { useRecords } from "../../../api/useRecords";
import { DASHBOARD_COLORS } from "../../../dashboard-framework/colors";
import { formatEuros } from "../../../dashboard-framework/format";
import { businessDaysExcludingSundays, resolveDatePreset, resolveRequiredDateFilterRange } from "../../../dashboard-framework/datePresets";
import { drillDownFromAggregate } from "../../../dashboard-framework/drilldown";
import { formatDateRangePt } from "../../../dashboard-framework/describeWidgetLogic";
import { DashboardPage } from "../../../dashboard-framework/DashboardPage";
import { monthLabelFromIndex } from "../../../dashboard-framework/aggregateRows";
import { PivotNode, TOTAL_COLUMN_KEY } from "../../../dashboard-framework/pivot";
import { AggregateQuerySpec, GlobalFilterConfig } from "../../../dashboard-framework/types";
import { useAggregate } from "../../../dashboard-framework/useAggregate";
import { useGlobalFilterValues } from "../../../dashboard-framework/useGlobalFilterValues";
import { PivotTableCard } from "../../../dashboard-framework/widgets/PivotTableCard";
import { StatCard } from "../../../dashboard-framework/widgets/StatCard";
import { ComposedBarLineChart } from "../../../dashboard-framework/widgets/ComposedBarLineChart";
import { WidgetCard } from "../../../dashboard-framework/widgets/WidgetCard";
import {
    banksDrillColumns,
    BANKS_DRILL_FIELDS,
    bankTransactionRoute,
    bankTransactionsDrillColumns,
    BANK_TRANSACTIONS_DRILL_FIELDS,
    orderPaymentRoute,
    ordersPaymentsDrillColumns,
    ORDERS_PAYMENTS_DRILL_FIELDS,
} from "../shared/drillColumns";
import {
    bankBalanceTotals,
    bankVsOrdersPivot,
    bankVsOrdersPivotItems,
    BankVsOrdersItem,
    CenterAllocationItem2,
    categoryRevenueCostPivot,
    centerAllocationPivot,
    centerPivotItems,
    companyPerformancePivot,
    companyPivotItems,
    CompanyPerformanceItem,
    EXCLUDED_NET_CATEGORY_NAMES,
    monthlyRevenueProjection,
    monthColumnKey,
    monthlySumsFromGroupedRows,
    revenueCostRunningPoints,
    singleSumAmount,
    todayRevenueByClinic,
    unmappedOrdersPivot,
    unmappedOrdersPivotItems,
    UnmappedOrderItem,
    withoutLoanDisbursements,
} from "./financeRows";
import {
    DEVIATION_MINIMUM_EUROS,
    formatDeviationTooltip,
    monthlyDeviation,
    MonthlyDeviationContext,
    TYPICAL_MONTH_COLUMN_KEY,
    TYPICAL_MONTH_COLUMN_LABEL,
} from "./financeMonthlyDeviation";

// Dashboard cross-backend: transações bancárias/centros (finance API) + encomendas (API principal).

const NEUROIMPROVE_COMPANY_ID = 1;

// Filtros dos pivots "resultado": exclui transferências entre contas próprias e pagamentos
// assumidos por parceiros; transações sem categoria contam.
const EXCLUDE_NET_FILTER = {
    _or: [{ category: { _null: true } }, { category: { name: { _nin: EXCLUDED_NET_CATEGORY_NAMES } } }],
};
// Filtro do KPI "Receitas Bancárias Este Mês" (só exclui transferências).
const EXCLUDE_TRANSFERS_FILTER = {
    _or: [{ category: { _null: true } }, { category: { name: { _neq: "Transfer Between Accounts" } } }],
};

// Gráfico "Receitas, despesas e resultado acumulado": Receitas (positivas) e Despesas
// (negativas) por mês de TODAS as empresas, mais o resultado acumulado no período. Duas queries por
// sinal do valor (data da transação, mesmas exclusões da família "resultado"); o acumulado é a soma
// mês a mês (origem 0 no início do período).
const allCompaniesRevenueQuery: AggregateQuerySpec = {
    entity: "bank_transactions",
    aggregate: { sum: "amount" },
    groupBy: ["year(transaction_date)", "month(transaction_date)"],
    fixedFilter: { _and: [{ amount: { _gt: 0 } }, EXCLUDE_NET_FILTER] },
    dataScope: "global",
};
const allCompaniesCostQuery: AggregateQuerySpec = {
    entity: "bank_transactions",
    aggregate: { sum: "amount" },
    groupBy: ["year(transaction_date)", "month(transaction_date)"],
    fixedFilter: { _and: [{ amount: { _lt: 0 } }, EXCLUDE_NET_FILTER] },
    dataScope: "global",
};

// KPI "Receitas Bancárias Este Mês": receitas (positivas) da MorningAbility este mês, sem transferências.
const monthBankRevenueQuery: AggregateQuerySpec = {
    entity: "bank_transactions",
    aggregate: { sum: "amount" },
    fixedFilter: { _and: [{ amount: { _gt: 0 } }, { company: { _eq: NEUROIMPROVE_COMPANY_ID } }, EXCLUDE_TRANSFERS_FILTER] },
    dataScope: "global",
};

const monthPaymentsQuery: AggregateQuerySpec = {
    entity: "orders_payments",
    aggregate: { sum: "amount" },
    dataScope: "global",
};

// "Encomendas sem transação bancária": pagamentos de encomendas SEM transação bancária associada,
// com método mapeável a uma categoria bancária, anos 2025/2026 — pivot Mês (data do pagamento) →
// encomenda. Buscadas linha a linha (não agregadas) para poder expandir por encomenda.
const ORDERS_WITHOUT_TRANSACTION_METHODS = ["multibanco", "transferencia_bancaria", "online_payment", "cash", "loan", "reembolso"];
const UNMAPPED_ORDERS_FIELDS = "id,payment_date,amount,order,payment_method";

// "Transações bancárias vs Encomendas": por método de pagamento, total das encomendas vs total das
// transações bancárias da categoria mapeada. Janela 2025/2026, excluindo o próprio dia.
const BANK_VS_ORDERS_METHODS = [
    { label: "Dinheiro", paymentMethod: "cash", categoryId: 92 },
    { label: "Multibanco", paymentMethod: "multibanco", categoryId: 91 },
    { label: "Pagamento Online", paymentMethod: "online_payment", categoryId: 117 },
    { label: "Transferência Bancária", paymentMethod: "transferencia_bancaria", categoryId: 93 },
];
const bankVsOrdersOrdersQuery: AggregateQuerySpec = {
    entity: "orders_payments",
    aggregate: { sum: "amount" },
    groupBy: ["payment_method", "year(payment_date)", "month(payment_date)"],
    fixedFilter: { payment_method: { _in: BANK_VS_ORDERS_METHODS.map((method) => method.paymentMethod) } },
    dataScope: "global",
};
const bankVsOrdersBankQuery = (todayISO: string): AggregateQuerySpec => ({
    entity: "bank_transactions",
    aggregate: { sum: "amount" },
    groupBy: ["category", "year(document_date)", "month(document_date)"],
    fixedFilter: {
        _and: [{ category: { _in: BANK_VS_ORDERS_METHODS.map((method) => method.categoryId) } }, { transaction_date: { _lt: todayISO } }],
    },
    dataScope: "global",
});
// Spec base do drill da coluna Banco — SEM fixedFilter para o filtro combinado ficar PLANO (um só `_and`).
// (Um `_and` aninhado noutro `_and` faz o endpoint /drill da extensão devolver 0 linhas.)
const bankDrillSpec: AggregateQuerySpec = { entity: "bank_transactions", aggregate: { sum: "amount" }, dataScope: "global" };

const DEFAULT_SENSITIVITY_PERCENT = 10;

const financeGlobalFilters: Array<GlobalFilterConfig> = [
    {
        key: "timeline",
        type: "datePreset",
        label: "Período",
        field: "transaction_date",
        defaultPreset: "ultimos_6_meses",
        presets: ["ultimos_3_meses", "ultimos_6_meses", "este_ano", "ultimos_12_meses"],
    },
    {
        key: "sensitivity",
        type: "choice",
        label: "Sensibilidade",
        defaultValue: String(DEFAULT_SENSITIVITY_PERCENT),
        options: [
            { value: "5", label: "5%" },
            { value: "10", label: "10%" },
            { value: "15", label: "15%" },
            { value: "20", label: "20%" },
            { value: "25", label: "25%" },
            { value: "40", label: "40%" },
        ],
    },
];

// Seta ao lado do valor quando o mês foge ao mês típico da linha: verde quando é melhor para o
// resultado (gastou menos / recebeu mais), vermelha quando é pior. Só existe nas rubricas de
// Despesa/Receita: numa linha de resultado líquido a mediana é a diferença entre dois fluxos grandes,
// logo é pequena por construção e quase todos os meses a ultrapassariam em percentagem. Essas linhas
// são também as únicas pintadas pelo semáforo de resultado, por isso as setas nunca caem sobre cor.
const deviationArrow = <ItemType,>(node: PivotNode<ItemType>, columnKey: string, deviationContext: MonthlyDeviationContext) => {
    const deviation = monthlyDeviation(node, columnKey, deviationContext);
    if (!deviation) return null;
    const tooltip = formatDeviationTooltip(deviation, monthLabelFromIndex(Number(columnKey)));
    const arrowSx = { fontSize: 16, color: deviation.impact === "better" ? DASHBOARD_COLORS.done : DASHBOARD_COLORS.cancelled };
    const arrow = (
        <Tooltip title={tooltip}>
            {deviation.direction === "up" ? <ArrowUpward sx={arrowSx} /> : <ArrowDownward sx={arrowSx} />}
        </Tooltip>
    );
    return arrow;
};

export const FinanceDashboard = () => {
    const { values, setValue } = useGlobalFilterValues(financeGlobalFilters);

    const now = new Date();
    const todayISO = format(now, "yyyy-MM-dd");
    const timelineRange = resolveRequiredDateFilterRange(values, "timeline", "ultimos_6_meses", now);

    const transactionDateFilter = { transaction_date: { _gte: timelineRange.gte, _lt: timelineRange.lt } };

    // Receitas/Despesas/resultado acumulado (todas as empresas, transaction_date, dentro do período)
    const allCompaniesRevenueResult = useAggregate(allCompaniesRevenueQuery, { filter: transactionDateFilter });
    const allCompaniesCostResult = useAggregate(allCompaniesCostQuery, { filter: transactionDateFilter });

    // Resumo por empresa (todas as empresas, valores PUROS por data da transação): transações ao nível
    // da linha (empresa/categoria/fornecedor) que alimentam o pivot expansível e o drill de célula.
    // Exclui transferências entre contas próprias e pagamentos assumidos por parceiros.
    const companyTransactionsResult = useQuery({
        queryKey: ["finance-company-transactions", timelineRange.gte, timelineRange.lt],
        queryFn: () =>
            getItems(
                "bank_transactions",
                "id,amount,description,transaction_date,company.name,category.name,category.parent_transaction_category.name,vendor.name",
                {
                    filter: { _and: [transactionDateFilter, EXCLUDE_NET_FILTER] },
                    pageSize: 8000,
                    sort: "id",
                }
            ),
        staleTime: 5 * 60 * 1000,
    });

    // "Custos por centro" + "Custos e receitas globais" partilham este fetch: transações da MorningAbility (data do documento) com a lista de centros
    // alocados (relação inversa operational_centers) e a categoria/categoria mãe. Cada transação é
    // repartida pelos centros (valor × percentagem); sem centro → "-No Value-". Só transações com
    // categoria e sem transferências entre contas próprias.
    const centerTransactionsResult = useQuery({
        queryKey: ["finance-center-transactions", timelineRange.gte, timelineRange.lt],
        queryFn: () =>
            getItems(
                "bank_transactions",
                "id,amount,description,document_date,category.name,category.parent_transaction_category.name,vendor.name,operational_centers.name,operational_centers.percentage",
                {
                    filter: {
                        _and: [
                            { company: { _eq: NEUROIMPROVE_COMPANY_ID } },
                            { document_date: { _gte: timelineRange.gte, _lt: timelineRange.lt } },
                            { category: { name: { _neq: "Transfer Between Accounts" } } },
                        ],
                    },
                    pageSize: 8000,
                    sort: "id",
                }
            ),
        staleTime: 5 * 60 * 1000,
    });

    // KPIs de mês/saldos (não respondem ao período)
    const monthRange = resolveDatePreset("este_mes", now);
    const monthPaymentsFilter = { payment_date: { _gte: monthRange.gte, _lt: monthRange.lt } };
    const monthBankRevenueFilter = { transaction_date: { _gte: monthRange.gte, _lt: monthRange.lt } };
    const monthPaymentsResult = useAggregate(monthPaymentsQuery, { filter: monthPaymentsFilter });
    const monthBankRevenueResult = useAggregate(monthBankRevenueQuery, { filter: monthBankRevenueFilter });
    const banksResult = useRecords("banks", "id,name,balance,company");

    // Receita de Hoje: pagamentos de encomendas com data de hoje (linha a linha, com a clínica da
    // encomenda) — total + repartição por clínica em chips. Janela própria (hoje), atualiza ao longo do dia.
    const todayRevenueResult = useQuery({
        queryKey: ["finance-today-revenue", todayISO],
        queryFn: () => getItems("orders_payments", "id,amount,payment_date,order.clinic.name", { filter: { payment_date: { _eq: todayISO } }, pageSize: 500, sort: "-id" }),
        staleTime: 60 * 1000,
    });

    // Orders without transaction / Bank vs Orders — respondem ao filtro de Período global (data de
    // pagamento / data de documento dentro do intervalo selecionado), como os restantes widgets.
    // Encomendas por conciliar linha a linha (pivot Mês → encomenda, expansível + link à encomenda).
    const unmappedOrdersResult = useQuery({
        queryKey: ["finance-unmapped-orders", timelineRange.gte, timelineRange.lt],
        queryFn: () =>
            getItems("orders_payments", UNMAPPED_ORDERS_FIELDS, {
                filter: {
                    _and: [
                        { connected_bank_transaction_id: { _null: true } },
                        { payment_method: { _in: ORDERS_WITHOUT_TRANSACTION_METHODS } },
                        { payment_date: { _gte: timelineRange.gte, _lt: timelineRange.lt } },
                    ],
                },
                pageSize: 3000,
                sort: "payment_date",
            }),
        staleTime: 5 * 60 * 1000,
    });
    const bankVsOrdersOrdersResult = useAggregate(bankVsOrdersOrdersQuery, { filter: { payment_date: { _gte: timelineRange.gte, _lt: timelineRange.lt } } });
    const bankVsOrdersBankResult = useAggregate(bankVsOrdersBankQuery(todayISO), { filter: { document_date: { _gte: timelineRange.gte, _lt: timelineRange.lt } } });

    const firstMonthIndex = Number(timelineRange.gte.slice(0, 4)) * 12 + (Number(timelineRange.gte.slice(5, 7)) - 1);
    const nowMonthIndex = now.getFullYear() * 12 + now.getMonth();
    const lastMonthIndex = Math.max(firstMonthIndex, nowMonthIndex);

    const currentMonthColumnKey = monthColumnKey(nowMonthIndex);
    const deviationContext: MonthlyDeviationContext = {
        currentMonthColumnKey,
        thresholdPercent: Number(values.sensitivity ?? DEFAULT_SENSITIVITY_PERCENT),
    };
    const isNotTypicalColumn = <ItemType,>(_node: PivotNode<ItemType>, columnKey: string) => columnKey !== TYPICAL_MONTH_COLUMN_KEY;
    const monthDeviationArrow = <ItemType,>(node: PivotNode<ItemType>, columnKey: string) => deviationArrow(node, columnKey, deviationContext);

    const centerItems = centerPivotItems(centerTransactionsResult.data?.items || []);
    const centerPivot = centerAllocationPivot(centerItems, currentMonthColumnKey);
    const categoryPivot = categoryRevenueCostPivot(withoutLoanDisbursements(centerItems), currentMonthColumnKey);

    const revenueByMonth = monthlySumsFromGroupedRows(allCompaniesRevenueResult.data || [], "transaction_date");
    const costByMonth = monthlySumsFromGroupedRows(allCompaniesCostResult.data || [], "transaction_date");
    const runningTotalPoints = revenueCostRunningPoints(revenueByMonth, costByMonth, firstMonthIndex, lastMonthIndex);

    const companyItems = companyPivotItems(companyTransactionsResult.data?.items || []);
    const companyPivot = companyPerformancePivot(companyItems, currentMonthColumnKey);

    const unmappedOrdersItems = unmappedOrdersPivotItems(unmappedOrdersResult.data?.items || []);
    const unmappedOrdersPivotResult = unmappedOrdersPivot(unmappedOrdersItems);
    const bankVsOrdersItems = bankVsOrdersPivotItems(bankVsOrdersOrdersResult.data || [], bankVsOrdersBankResult.data || [], BANK_VS_ORDERS_METHODS);
    const bankVsOrdersPivotResult = bankVsOrdersPivot(bankVsOrdersItems, BANK_VS_ORDERS_METHODS);

    const todayRevenue = todayRevenueByClinic(todayRevenueResult.data?.items || []);
    const todayRevenueChips = todayRevenue.clinics.map((entry) => `${entry.clinic}: ${formatEuros(entry.amount)}`);

    const businessDaysElapsed = businessDaysExcludingSundays({ gte: monthRange.gte, lt: format(addDays(now, 1), "yyyy-MM-dd") });
    const businessDaysInMonth = businessDaysExcludingSundays(monthRange);
    const paymentsThisMonth = singleSumAmount(monthPaymentsResult.data);
    const revenueProjection = monthlyRevenueProjection({ paymentsTotal: paymentsThisMonth, businessDaysElapsed, businessDaysInMonth });
    const bankRevenueThisMonth = Math.round(singleSumAmount(monthBankRevenueResult.data) * 100) / 100;
    const balances = bankBalanceTotals(banksResult.data?.items || [], NEUROIMPROVE_COMPANY_ID);
    const eurosLabel = (value: number | null) => (value === null ? undefined : formatEuros(value));

    const periodText = `Considera transações com data ${formatDateRangePt(timelineRange)}`;
    const deviationText =
        `Coluna "${TYPICAL_MONTH_COLUMN_LABEL}": mediana dos meses com movimento dentro do período visível (mudar o período muda o típico). ` +
        `Uma seta ▲/▼ marca os meses que se afastam desse típico mais de ${deviationContext.thresholdPercent}% E mais de ${formatEuros(DEVIATION_MINIMUM_EUROS)} — ` +
        `▲ = mais dinheiro na rubrica (gastou/recebeu mais), ▼ = menos; verde quando é melhor para o resultado, vermelho quando é pior. Passa o rato na seta para ver as contas.`;
    const deviationNote =
        `As setas só existem nas rubricas de Despesa/Receita: numa linha de resultado (Total, empresa, centro) a mediana é a diferença entre dois fluxos grandes, ` +
        `logo quase todos os meses a ultrapassariam em percentagem. O mês típico de uma linha não é a soma dos típicos das linhas filhas.`;
    const partialMonthWarning = `O mês corrente está incompleto: fica fora do cálculo do mês típico e nunca leva seta (cabeçalho "parcial").`;

    const explanations = {
        todayRevenue: {
            queryDescription: `Total dos pagamentos de encomendas com data de hoje (${todayISO}), a partir de orders_payments. Os chips mostram a repartição por clínica (clínica da encomenda). Atualiza ao longo do dia; não responde ao filtro de período.`,
        },
        revenueProjection: {
            queryDescription:
                `Pagamentos de encomendas deste mês (${formatDateRangePt(monthRange)}) ÷ dias úteis decorridos (segunda a sábado, hoje incluído: ${businessDaysElapsed}) = média por dia útil (chip "Média/dia útil"), × dias úteis do mês (${businessDaysInMonth}) = projeção otimista. ` +
                `A projeção negativa é essa projeção × 0,90. Não responde ao filtro de período.`,
        },
        bankRevenueMonth: {
            queryDescription: `Soma das transações bancárias com valor positivo deste mês (${formatDateRangePt(monthRange)}) da MorningAbility, excluindo transferências entre contas próprias. Não responde ao filtro de período.`,
        },
        moneyAllAccounts: {
            queryDescription: `Soma do saldo atual de todas as contas bancárias registadas (campo "balance" da coleção banks). A lista de detalhe mostra apenas as contas ativas. Não responde ao filtro de período.`,
        },
        moneyNeuroImprove: {
            queryDescription: `Soma do saldo atual das contas bancárias da empresa NeuroImprove/MorningAbility (company = ${NEUROIMPROVE_COMPANY_ID}). Não responde ao filtro de período.`,
        },
        categoryRevenueCost: {
            queryDescription: `Custos e receitas da MorningAbility por Tipo (Despesa/Receita) → categoria mãe → categoria → fornecedor/cliente, e por mês (data do documento). Valores ALOCADOS a centros de custo (valor da transação × percentagem do centro). A linha Total (Resultado) = receitas + custos. ${periodText}. ${deviationText}`,
            businessNote: `Clica em Despesa/Receita ou numa categoria para expandir até ao fornecedor. ${deviationNote}`,
            warningNote:
                "Filtros: só a empresa MorningAbility; exclui a categoria \"Transfer Between Accounts\" (transferências entre contas próprias); só transações COM categoria (não-categorizadas excluídas); os empréstimos recebidos (\"Creditos\" com valor positivo) não contam como receita — as prestações continuam nas despesas. Todas as outras categorias entram (incluindo Sócios, etc.). " +
                partialMonthWarning,
        },
        runningTotal: {
            queryDescription: `Por mês e para TODAS as empresas (data da transação): Receitas (soma das transações positivas) e Despesas (soma das negativas, em módulo) em barras, e o Resultado acumulado (linha) — soma mês a mês de receitas + custos ao longo do período (${formatDateRangePt(timelineRange)}). Exclui transferências entre contas e pagamentos por parceiro.`,
            warningNote: "A linha acumula a partir do início do período selecionado (começa em 0); mudar o período muda o ponto de partida.",
        },
        companies: {
            queryDescription: `Resultado por empresa a partir dos valores PUROS das transações bancárias (sem alocação a centros), por mês (data da transação). Expansível por Empresa → Tipo (Receita/Despesa) → categoria mãe → categoria → fornecedor/cliente. A linha de cada empresa mostra o resultado líquido (receitas + custos). ${periodText}. ${deviationText}`,
            businessNote: `Clica numa empresa ou num nó para expandir; clica num valor para ver as transações subjacentes. ${deviationNote}`,
            warningNote:
                "Exclui as categorias \"Transfer Between Accounts\" (transferências entre contas próprias) e \"Paid by partner\" (pagamentos assumidos por parceiros). Transações sem categoria contam na mesma. " +
                partialMonthWarning,
        },
        centers: {
            queryDescription: `Valor alocado por centro operacional (data do documento, MorningAbility): para cada transação, valor × percentagem de cada centro; transações sem centro em "-No Value-". Expansível por Centro → Despesa/Receita → categoria mãe → categoria → fornecedor/cliente. Exclui transferências entre contas próprias. ${periodText}. ${deviationText}`,
            businessNote: `Clica num nó para expandir. ${deviationNote}`,
            warningNote: partialMonthWarning,
        },
        ordersWithout: {
            queryDescription: `Pagamentos de encomendas SEM transação bancária associada (por conciliar), por Mês (data do pagamento) → encomenda. Considera apenas métodos de pagamento mapeáveis a uma categoria bancária. Responde ao filtro de Período.`,
            businessNote: "Expande um mês para ver as encomendas por conciliar desse mês; cada encomenda liga a /orders.",
        },
        bankVsOrders: {
            queryDescription: `Por Método → Mês: total dos pagamentos de encomendas (Encomendas, por data de pagamento) vs total das transações bancárias da categoria correspondente (Banco, por data de documento). Dif = Banco − Encomendas; % = (Encomendas − Banco) / Encomendas. Responde ao filtro de Período.`,
            businessNote: "Expande um método para ver por mês; clica numa célula de Encomendas ou Banco para ver os registos por trás desse número (com link).",
        },
    };

    const euroAmountHeader = { label: "Soma", aggregate: { sum: "amount" }, format: formatEuros };
    const drills = {
        todayRevenue: drillDownFromAggregate(monthPaymentsQuery, { payment_date: { _eq: todayISO } }, {
            title: `Receita de Hoje (${todayISO})`,
            queryDescription: explanations.todayRevenue.queryDescription,
            fieldsQuery: ORDERS_PAYMENTS_DRILL_FIELDS,
            defaultSort: "-payment_date",
            columns: ordersPaymentsDrillColumns,
            rowRoute: orderPaymentRoute,
            headerAggregate: euroAmountHeader,
        }),
        revenueProjection: drillDownFromAggregate(monthPaymentsQuery, monthPaymentsFilter, {
            title: "Pagamentos de encomendas deste mês",
            queryDescription: `${explanations.revenueProjection.queryDescription} A soma da modal é o total de pagamentos do mês — o cartão mostra a projeção calculada a partir dela.`,
            fieldsQuery: ORDERS_PAYMENTS_DRILL_FIELDS,
            defaultSort: "-payment_date",
            columns: ordersPaymentsDrillColumns,
            rowRoute: orderPaymentRoute,
            headerAggregate: euroAmountHeader,
        }),
        bankRevenueMonth: drillDownFromAggregate(monthBankRevenueQuery, monthBankRevenueFilter, {
            title: "Receitas Bancárias Este Mês",
            queryDescription: explanations.bankRevenueMonth.queryDescription,
            fieldsQuery: BANK_TRANSACTIONS_DRILL_FIELDS,
            defaultSort: "-transaction_date",
            columns: bankTransactionsDrillColumns,
            rowRoute: bankTransactionRoute,
            headerAggregate: euroAmountHeader,
        }),
        moneyAllAccounts: {
            kind: "query" as const,
            title: "Dinheiro em Todas as Contas",
            queryDescription: `Saldo atual das contas bancárias ativas (campo "balance" da coleção banks, "is_active" = verdadeiro), por ordem alfabética. Não responde ao filtro de período.`,
            entity: "banks",
            fieldsQuery: BANKS_DRILL_FIELDS,
            filter: { is_active: { _eq: true } },
            defaultSort: "name",
            columns: banksDrillColumns,
            headerAggregate: { label: "Soma", aggregate: { sum: "balance" }, format: formatEuros },
        },
        moneyNeuroImprove: {
            kind: "query" as const,
            title: "Dinheiro nas Contas NeuroImprove",
            queryDescription: explanations.moneyNeuroImprove.queryDescription,
            entity: "banks",
            fieldsQuery: BANKS_DRILL_FIELDS,
            filter: { company: { _eq: NEUROIMPROVE_COMPANY_ID } },
            defaultSort: "name",
            columns: banksDrillColumns,
            headerAggregate: { label: "Soma", aggregate: { sum: "balance" }, format: formatEuros },
        },
    };

    const monthIndexDateRange = (monthIndex: number) => {
        const year = Math.floor(monthIndex / 12);
        const month = monthIndex % 12;
        const range = { gte: format(new Date(year, month, 1), "yyyy-MM-dd"), lt: format(new Date(year, month + 1, 1), "yyyy-MM-dd") };
        return range;
    };

    // Drill de célula do "Resumo por empresa": lista as transações bancárias (valor puro) que compõem
    // a célula clicada. A soma das linhas iguala o valor da célula por construção.
    const companyCellDrillDown = (node: PivotNode<CompanyPerformanceItem>, columnKey: string) => {
        const cellItems = columnKey === TOTAL_COLUMN_KEY ? node.items : node.itemsByColumn?.[columnKey] || [];
        if (cellItems.length === 0) return null;
        const columnLabel = columnKey === TOTAL_COLUMN_KEY ? "Total" : monthLabelFromIndex(Number(columnKey));
        const total = cellItems.reduce((sum, item) => sum + item.amount, 0);
        const rows = [...cellItems]
            .sort((first, second) => first.amount - second.amount)
            .map((item) => ({
                id: item.id,
                transactionDate: item.transactionDate ? format(new Date(item.transactionDate), "dd/MM/yyyy") : null,
                description: item.description,
                category: item.categoryName,
                vendor: item.vendorName,
                valor: formatEuros(item.amount),
            }));
        const drill = {
            kind: "rows" as const,
            title: `${node.path.join(" › ")} — ${columnLabel}`,
            queryDescription:
                "Transações bancárias subjacentes desta célula (valor puro, sem alocação a centros). A soma das linhas iguala o valor da célula.",
            columns: [
                { key: "transactionDate", label: "Data" },
                { key: "description", label: "Descrição" },
                { key: "category", label: "Categoria" },
                { key: "vendor", label: "Fornecedor / Cliente" },
                { key: "valor", label: "Valor", isNumeric: true },
            ],
            rows,
            headerChips: [`Soma: ${formatEuros(Math.round(total * 100) / 100)}`],
            rowRoute: bankTransactionRoute,
        };
        return drill;
    };

    // Drill de célula do "Transações bancárias vs Encomendas": mostra os registos por trás do número
    // clicado, no âmbito da linha (método, e mês se expandido). Encomendas → pagamentos de encomendas;
    // Banco → transações bancárias da categoria. A soma da modal iguala a célula clicada.
    const bankVsOrdersCellDrillDown = (node: PivotNode<BankVsOrdersItem>, columnKey: string) => {
        const method = BANK_VS_ORDERS_METHODS.find((candidate) => candidate.label === node.path[0]);
        if (!method) return null;
        const dateRange = node.depth === 1 && node.items[0] ? monthIndexDateRange(node.items[0].monthIndex) : { gte: timelineRange.gte, lt: timelineRange.lt };
        const scopeLabel = node.path.slice(1).join(" · ");
        const suffix = scopeLabel ? ` · ${scopeLabel}` : "";
        if (columnKey === "bank") {
            const bankDrill = drillDownFromAggregate(
                bankDrillSpec,
                { _and: [{ category: { _eq: method.categoryId } }, { document_date: { _gte: dateRange.gte, _lt: dateRange.lt } }, { transaction_date: { _lt: todayISO } }] },
                {
                    title: `Banco — ${method.label}${suffix}`,
                    queryDescription: `Transações bancárias da categoria correspondente a ${method.label}${scopeLabel ? `, em ${scopeLabel}` : ""}. A soma corresponde à coluna Banco.`,
                    fieldsQuery: BANK_TRANSACTIONS_DRILL_FIELDS,
                    defaultSort: "-transaction_date",
                    columns: bankTransactionsDrillColumns,
                    rowRoute: bankTransactionRoute,
                    headerAggregate: euroAmountHeader,
                }
            );
            return bankDrill;
        }
        const ordersDrill = drillDownFromAggregate(
            bankVsOrdersOrdersQuery,
            { _and: [{ payment_method: { _eq: method.paymentMethod } }, { payment_date: { _gte: dateRange.gte, _lt: dateRange.lt } }] },
            {
                title: `Encomendas — ${method.label}${suffix}`,
                queryDescription: `Pagamentos de encomendas com método ${method.label}${scopeLabel ? `, em ${scopeLabel}` : ""}. A soma corresponde à coluna Encomendas.`,
                fieldsQuery: ORDERS_PAYMENTS_DRILL_FIELDS,
                defaultSort: "-payment_date",
                columns: ordersPaymentsDrillColumns,
                rowRoute: orderPaymentRoute,
                headerAggregate: euroAmountHeader,
            }
        );
        return ordersDrill;
    };

    // Rótulo do nó folha (encomenda) do pivot "Encomendas sem transação": link para a encomenda
    // (cor herdada do texto, sublinhado só no hover — sem a cor de link default).
    const renderUnmappedOrderLabel = (node: PivotNode<UnmappedOrderItem>) => {
        const orderId = node.depth === 1 ? node.items[0]?.orderId ?? null : null;
        if (orderId === null) return node.label;
        const link = (
            <Link href={`/orders/${orderId}`} target="_blank" rel="noopener" underline="hover" color="inherit">
                {node.label}
            </Link>
        );
        return link;
    };

    // Drill de célula dos pivots alocados ("Custos e receitas globais" e "Custos por centro"): lista as
    // fatias de alocação (uma linha por transação × centro) que compõem a célula clicada. A soma das
    // linhas iguala o valor da célula por construção.
    const allocationCellDrillDown = (node: PivotNode<CenterAllocationItem2>, columnKey: string) => {
        const cellItems = columnKey === TOTAL_COLUMN_KEY ? node.items : node.itemsByColumn?.[columnKey] || [];
        if (cellItems.length === 0) return null;
        const columnLabel = columnKey === TOTAL_COLUMN_KEY ? "Total" : monthLabelFromIndex(Number(columnKey));
        const total = cellItems.reduce((sum, item) => sum + item.allocated, 0);
        const rows = [...cellItems]
            .sort((first, second) => first.allocated - second.allocated)
            .map((item) => ({
                id: item.id,
                documentDate: item.documentDate ? format(new Date(item.documentDate), "dd/MM/yyyy") : null,
                description: item.description,
                category: item.categoryName,
                center: item.center,
                percentage: item.percentage === null ? "—" : `${item.percentage}%`,
                valor: formatEuros(item.allocated),
            }));
        const drill = {
            kind: "rows" as const,
            title: `${node.path.join(" › ")} — ${columnLabel}`,
            queryDescription:
                "Transações subjacentes desta célula (uma linha por centro alocado; valor = valor da transação × percentagem do centro). A soma das linhas iguala o valor da célula.",
            columns: [
                { key: "documentDate", label: "Data" },
                { key: "description", label: "Descrição" },
                { key: "category", label: "Categoria" },
                { key: "center", label: "Centro" },
                { key: "percentage", label: "%", isNumeric: true },
                { key: "valor", label: "Valor", isNumeric: true },
            ],
            rows,
            headerChips: [`Soma: ${formatEuros(Math.round(total * 100) / 100)}`],
            rowRoute: bankTransactionRoute,
        };
        return drill;
    };

    return (
        <DashboardPage globalFilters={financeGlobalFilters} values={values} onChange={setValue}>
            <Box display="grid" gridTemplateColumns="repeat(auto-fit, minmax(220px, 1fr))" gap={3}>
                <StatCard
                    label="Receita de Hoje"
                    value={todayRevenueResult.data ? formatEuros(todayRevenue.total) : undefined}
                    chips={todayRevenueChips}
                    color={DASHBOARD_COLORS.highlight}
                    explanation={explanations.todayRevenue}
                    isLoading={todayRevenueResult.isLoading}
                    drillDown={drills.todayRevenue}
                />
                <StatCard
                    label="Projeção de Receita Mensal (otimista)"
                    value={monthPaymentsResult.data ? eurosLabel(revenueProjection?.optimistic ?? null) : undefined}
                    chips={
                        monthPaymentsResult.data && revenueProjection
                            ? [`Média/dia útil: ${formatEuros(revenueProjection.dailyAverage)}`, `Negativa: ${formatEuros(revenueProjection.pessimistic)}`]
                            : undefined
                    }
                    color={DASHBOARD_COLORS.done}
                    explanation={explanations.revenueProjection}
                    isLoading={monthPaymentsResult.isLoading}
                    drillDown={drills.revenueProjection}
                />
                <StatCard
                    label="Receitas Bancárias Este Mês"
                    value={monthBankRevenueResult.data ? eurosLabel(bankRevenueThisMonth) : undefined}
                    color={DASHBOARD_COLORS.done}
                    explanation={explanations.bankRevenueMonth}
                    isLoading={monthBankRevenueResult.isLoading}
                    drillDown={drills.bankRevenueMonth}
                />
                <StatCard
                    label="Dinheiro em Todas as Contas"
                    value={banksResult.data ? eurosLabel(balances.allAccounts) : undefined}
                    color={DASHBOARD_COLORS.toCome}
                    explanation={explanations.moneyAllAccounts}
                    isLoading={banksResult.isLoading}
                    drillDown={drills.moneyAllAccounts}
                />
                <StatCard
                    label="Dinheiro nas Contas NeuroImprove"
                    value={banksResult.data ? eurosLabel(balances.neuroImproveAccounts) : undefined}
                    color={DASHBOARD_COLORS.orange}
                    explanation={explanations.moneyNeuroImprove}
                    isLoading={banksResult.isLoading}
                    drillDown={drills.moneyNeuroImprove}
                />
            </Box>

            <PivotTableCard
                title="Custos e receitas globais"
                explanation={explanations.categoryRevenueCost}
                pivot={categoryPivot}
                rowHeaderLabel="Tipo / Categoria mãe / Categoria / Fornecedor"
                isLoading={centerTransactionsResult.isLoading}
                isError={centerTransactionsResult.isError}
                cellDrillDown={allocationCellDrillDown}
                cellIsClickable={isNotTypicalColumn}
                cellAdornment={monthDeviationArrow}
            />

            <PivotTableCard
                title="Custos por centro"
                explanation={explanations.centers}
                pivot={centerPivot}
                rowHeaderLabel="Centro / Tipo / Categoria mãe / Categoria / Fornecedor"
                isLoading={centerTransactionsResult.isLoading}
                isError={centerTransactionsResult.isError}
                cellDrillDown={allocationCellDrillDown}
                cellIsClickable={isNotTypicalColumn}
                cellAdornment={monthDeviationArrow}
            />

            <PivotTableCard
                title="Resumo por empresa"
                explanation={explanations.companies}
                pivot={companyPivot}
                rowHeaderLabel="Empresa / Tipo / Categoria mãe / Categoria / Fornecedor"
                isLoading={companyTransactionsResult.isLoading}
                isError={companyTransactionsResult.isError}
                cellDrillDown={companyCellDrillDown}
                cellIsClickable={isNotTypicalColumn}
                cellAdornment={monthDeviationArrow}
            />

            <WidgetCard
                title="Receitas, despesas e resultado acumulado (todas as empresas)"
                explanation={explanations.runningTotal}
                bodyHeight={440}
                isLoading={allCompaniesRevenueResult.isLoading || allCompaniesCostResult.isLoading}
                isError={allCompaniesRevenueResult.isError || allCompaniesCostResult.isError}
            >
                <ComposedBarLineChart
                    points={runningTotalPoints}
                    barSeries={[
                        { key: "receitas", label: "Receitas", color: DASHBOARD_COLORS.done },
                        { key: "despesas", label: "Despesas", color: DASHBOARD_COLORS.cancelled },
                    ]}
                    lineSeries={[{ key: "resultado", label: "Resultado acumulado", color: DASHBOARD_COLORS.highlight, strokeWidth: 2 }]}
                    height={440}
                    valueFormatter={formatEuros}
                />
            </WidgetCard>

            <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                    <PivotTableCard
                        title="Encomendas sem transação bancária"
                        explanation={explanations.ordersWithout}
                        pivot={unmappedOrdersPivotResult}
                        rowHeaderLabel="Mês / Encomenda"
                        maxBodyHeight={420}
                        isLoading={unmappedOrdersResult.isLoading}
                        isError={unmappedOrdersResult.isError}
                        renderNodeLabel={renderUnmappedOrderLabel}
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <PivotTableCard
                        title="Transações bancárias vs Encomendas"
                        explanation={explanations.bankVsOrders}
                        pivot={bankVsOrdersPivotResult}
                        rowHeaderLabel="Método / Mês"
                        maxBodyHeight={420}
                        isLoading={bankVsOrdersOrdersResult.isLoading || bankVsOrdersBankResult.isLoading}
                        isError={bankVsOrdersOrdersResult.isError || bankVsOrdersBankResult.isError}
                        cellDrillDown={bankVsOrdersCellDrillDown}
                        cellIsClickable={(_node, columnKey) => columnKey === "orders" || columnKey === "bank"}
                    />
                </Grid>
            </Grid>
        </DashboardPage>
    );
};
